// INFLOW Marketing lead scoring — v1.
//
// Reads ONLY the existing survey answers (stored values, never labels or
// letter positions). Each answered question is worth 1-5 points; the score is
// the AVERAGE of the points earned, mapped onto 0-10:
//
//   raw_average = sum(points) / questions_answered
//   lead_score  = ((raw_average - 1) / 4) * 10   (one decimal, round-half-up)
//
// Averaging means a lead who happens to see more questions cannot out-score a
// stronger lead who saw fewer. Scores are snapshotted at submission time — do
// NOT recompute historical leads on read. If the table below changes, bump
// SCORING_VERSION and backfill deliberately.

export const SCORING_VERSION = "v1";

/** questionId -> stored answer value -> points (1-5). */
export const SCORE_POINTS: Record<string, Record<string, number>> = {
  // Q1 · Is the clinic the owner's only source of income?
  role: {
    "Detailing Is My Only Income": 5,
    "I Have A W2 Or Side Job": 3,
    "I'm In School On The Side": 1,
  },
  // Q2 · Hours a week you can commit
  website_status: {
    "30+ hours now": 5,
    "30+ hours within 2 weeks": 3,
    "Around 20 hours": 1,
  },
  // Q3 · Goal with the clinic
  deals_12mo: {
    "Go full time": 5,
    "Side thing only": 1,
  },
  // Q4 · What could you invest in growing the business?
  // Intentionally reversed relative to option order: higher budget = higher score.
  followup_system: {
    "<$500": 1,
    "$500–$1K": 2,
    "$1–2K": 3,
    "$2–3K": 4,
    "$3K+": 5,
  },
  // Q5 · Can you cover $20-30/day in ad spend?
  motivation: {
    "Yes, $100 is no problem": 5,
    "No, $100 would stress me": 1,
  },
  // --- Current clinic question set (Q1–Q3) ---
  clinic_owner: {
    "Yes - I'm a Clinic Owner": 5,
    "No - I'm going to leave this page": 1,
    "I'm a patient looking for a doctor": 1,
  },
  offers_treatment: {
    "Yes, I offer it now and have the equipment for it": 5,
    "I have the equipment but I'm not actively running it now": 4,
    "I don't offer it yet, but I'm looking to add it": 2,
    "No, I mainly do general chiropractic, adjustments, or PI": 1,
  },
  care_plan_price: {
    "$10,000+": 5,
    "$5,000 to $10,000": 4,
    "$3,000 to $5,000": 3,
    "$1,500 to $2,999": 2,
    "Less than $1,500": 1,
  },
};

export const SCORED_QUESTIONS = [
  { id: "role", label: "Only source of income" },
  { id: "website_status", label: "Hours per week" },
  { id: "deals_12mo", label: "Business goal" },
  { id: "followup_system", label: "Investment capacity" },
  { id: "motivation", label: "Ad spend capacity" },
  { id: "clinic_owner", label: "Clinic owner" },
  { id: "offers_treatment", label: "Offers treatment" },
  { id: "care_plan_price", label: "Lowest care plan price" },
] as const;

export type ScoreLineV1 = {
  questionId: string;
  label: string;
  value: string;
  points: number;
};

export type LeadScoreResultV1 = {
  /** 0.0-10.0 with one decimal, or null when nothing scorable was answered. */
  leadScore: number | null;
  /** Average points earned (1-5) with two decimals, or null. */
  rawAverage: number | null;
  questionsAnswered: number;
  scoringVersion: string;
  lines: ScoreLineV1[];
  /** Stored values that were not in the point table (scored as null). */
  unknownValues: { questionId: string; value: string }[];
};

/** Round half-up to `places` decimals, float-error tolerant. */
function roundHalfUp(n: number, places: number): number {
  const f = 10 ** places;
  return Math.round(n * f + 1e-9) / f;
}

export function computeLeadScoreV1(
  answers: Record<string, string | string[] | null | undefined>,
): LeadScoreResultV1 {
  const lines: ScoreLineV1[] = [];
  const unknownValues: { questionId: string; value: string }[] = [];
  let sum = 0;

  for (const q of SCORED_QUESTIONS) {
    const raw = answers[q.id];
    const value = Array.isArray(raw) ? raw[0] : raw;
    if (value == null || String(value).trim() === "") continue;
    const v = String(value).trim();
    const points = SCORE_POINTS[q.id]?.[v];
    if (points == null) {
      unknownValues.push({ questionId: q.id, value: v });
      console.warn(`[lead-score] unknown stored value for "${q.id}": ${JSON.stringify(v)} — scored as null`);
      continue;
    }
    lines.push({ questionId: q.id, label: q.label, value: v, points });
    sum += points;
  }

  // Any unrecognised value invalidates the score rather than silently
  // defaulting to a middling number.
  if (lines.length === 0 || unknownValues.length > 0) {
    return {
      leadScore: null,
      rawAverage: null,
      questionsAnswered: lines.length,
      scoringVersion: SCORING_VERSION,
      lines,
      unknownValues,
    };
  }

  const avg = sum / lines.length; // never rounded before the final step
  const score = ((avg - 1) / 4) * 10;
  return {
    leadScore: roundHalfUp(score, 1),
    rawAverage: roundHalfUp(avg, 2),
    questionsAnswered: lines.length,
    scoringVersion: SCORING_VERSION,
    lines,
    unknownValues,
  };
}

/** Display helper: 10.0 -> "10", 7.3 -> "7.3", null -> "—". */
export function formatScore(score: number | null | undefined): string {
  if (score == null || Number.isNaN(Number(score))) return "—";
  const n = Number(score);
  const s = n.toFixed(1);
  return s.endsWith(".0") ? s.slice(0, -2) : s;
}

export type ScoreBand = "hot" | "warm" | "cool" | "cold" | "none";

export function scoreBand(score: number | null | undefined): ScoreBand {
  if (score == null || Number.isNaN(Number(score))) return "none";
  const n = Number(score);
  if (n >= 8) return "hot";
  if (n >= 6) return "warm";
  if (n >= 4) return "cool";
  return "cold";
}

export const BAND_META: { key: Exclude<ScoreBand, "none">; label: string; range: string; chip: string; fill: string }[] = [
  { key: "hot", label: "Hot", range: "8.0–10", chip: "bg-emerald-500/15 text-emerald-400 border-emerald-500/40", fill: "#10b981" },
  { key: "warm", label: "Warm", range: "6.0–7.9", chip: "bg-yellow-500/15 text-yellow-400 border-yellow-500/40", fill: "#eab308" },
  { key: "cool", label: "Cool", range: "4.0–5.9", chip: "bg-orange-500/15 text-orange-400 border-orange-500/40", fill: "#f97316" },
  { key: "cold", label: "Cold", range: "0–3.9", chip: "bg-rose-500/15 text-rose-400 border-rose-500/40", fill: "#f43f5e" },
];

export const NO_SCORE_CHIP = "bg-slate-500/15 text-slate-400 border-slate-500/40";

export function bandMeta(band: ScoreBand) {
  return BAND_META.find((b) => b.key === band) ?? null;
}

/** Mean of the non-null scores only (nulls excluded from numerator AND denominator). */
export function averageScore(scores: (number | null | undefined)[]): { avg: number | null; n: number; excluded: number } {
  const vals = scores.filter((s): s is number => s != null && !Number.isNaN(Number(s))).map(Number);
  return {
    avg: vals.length ? roundHalfUp(vals.reduce((s, v) => s + v, 0) / vals.length, 1) : null,
    n: vals.length,
    excluded: scores.length - vals.length,
  };
}

export function bandCounts(scores: (number | null | undefined)[]): Record<Exclude<ScoreBand, "none">, number> {
  const out = { hot: 0, warm: 0, cool: 0, cold: 0 };
  for (const s of scores) {
    const b = scoreBand(s);
    if (b !== "none") out[b] += 1;
  }
  return out;
}

/**
 * Recover the scored answers from a persisted lead so the admin UI can show
 * the breakdown. `interest` holds Q1; the remaining answers are labelled lines
 * inside `message` (written by the survey submit handler).
 */
export function answersFromLead(lead: { interest?: string | null; message?: string | null }): Record<string, string | null> {
  const get = (label: string): string | null => {
    if (!lead.message) return null;
    const re = new RegExp(`(^|\\n)\\s*${label.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}:\\s*([^\\n\\r]+)`, "i");
    const m = lead.message.match(re);
    if (!m) return null;
    const v = m[2].trim();
    return !v || v === "-" ? null : v;
  };
  return {
    role: lead.interest ?? get("Role"),
    website_status: get("Website status"),
    deals_12mo: get("Monthly revenue"),
    followup_system: get("Liquid funds available"),
    motivation: get("$100 investment answer"),
    clinic_owner: get("Clinic owner"),
    offers_treatment: get("Offers treatment"),
    care_plan_price: get("Lowest care plan price"),
  };
}
